import { useEffect, useCallback, useRef } from 'react';
/* globals google */

const parseAddressComponents = (components) => {
  const result = {
    street1: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'US',
  };

  let streetNumber = '';
  let route = '';

  components.forEach((component) => {
    const types = component.types;

    if (types.includes('street_number')) {
      streetNumber = component.long_name;
    }
    if (types.includes('route')) {
      route = component.long_name;
    }
    if (types.includes('locality')) {
      result.city = component.long_name;
    }
    // Some addresses use sublocality instead of locality
    if (types.includes('sublocality_level_1') && !result.city) {
      result.city = component.long_name;
    }
    if (types.includes('administrative_area_level_1')) {
      result.state = component.short_name;
    }
    if (types.includes('postal_code')) {
      result.zipcode = component.long_name;
    }
    if (types.includes('country')) {
      result.country = component.short_name;
    }
  });

  result.street1 = [streetNumber, route].filter(Boolean).join(' ');

  return result;
};

// Maps calls window.gm_authFailure when the key is rejected for this origin
// (e.g. RefererNotAllowedMapError). The Places library still loads and the
// Autocomplete widget still attaches, but it can no longer fetch predictions —
// and while attached it fights the React-controlled street input, leaving the
// field impossible to type a full address into. Track the failure so the
// widget is detached and never re-attached; the address fields then behave as
// plain manual inputs. sessionStorage keeps that decision across reloads,
// since the rejection is a property of the origin, not of one page load.
const AUTH_FAILED_KEY = 'google-maps-auth-failed';
const POLL_MS = 200;
const MAX_WAIT_MS = 10000;

const readAuthFailed = () => {
  try {
    return 'true' === window.sessionStorage.getItem(AUTH_FAILED_KEY);
  } catch (e) {
    return false;
  }
};

let mapsAuthFailed = readAuthFailed();
const liveAutocompletes = new Set();

const detachAutocomplete = (autocomplete) => {
  if (!autocomplete || !window.google?.maps?.event) return;
  if (autocomplete.unbindAll) autocomplete.unbindAll();
  window.google.maps.event.clearInstanceListeners(autocomplete);
};

const handleAuthFailure = () => {
  mapsAuthFailed = true;
  try {
    window.sessionStorage.setItem(AUTH_FAILED_KEY, 'true');
  } catch (e) {
    // Private mode / storage disabled — the in-memory flag still holds.
  }
  liveAutocompletes.forEach(detachAutocomplete);
  liveAutocompletes.clear();
  document
    .querySelectorAll('.pac-container')
    .forEach((node) => node.parentNode && node.parentNode.removeChild(node));
};

export default function useGooglePlaces() {
  const autocompleteRef = useRef(null);

  useEffect(() => {
    window.gm_authFailure = handleAuthFailure;
  }, []);

  // Ensure Google Maps script with places library is loaded
  useEffect(() => {
    const existingScript = document.getElementById('googleMaps');

    if (existingScript) {
      // Check if places library is loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        return;
      }
      // If script exists but places not loaded, update src
      const currentSrc = existingScript.getAttribute('src');
      if (!currentSrc.includes('libraries=places')) {
        existingScript.setAttribute('src', currentSrc + '&libraries=places');
      }
      return;
    }

    const script = document.createElement('script');
    script.setAttribute('id', 'googleMaps');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
    document.getElementsByTagName('body')[0].appendChild(script);
  }, []);

  const initAutocomplete = useCallback((inputElement, onPlaceSelect) => {
    if (!inputElement || mapsAuthFailed) return;

    // Bounded wait for Google Maps; an unbounded poll would keep a timer alive
    // forever on origins where the script never loads at all.
    let waited = 0;

    const checkGoogle = () => {
      if (mapsAuthFailed) return;

      if (window.google && window.google.maps && window.google.maps.places) {
        // Clean up previous autocomplete
        if (autocompleteRef.current) {
          liveAutocompletes.delete(autocompleteRef.current);
          detachAutocomplete(autocompleteRef.current);
        }

        const autocomplete = new google.maps.places.Autocomplete(inputElement, {
          types: ['address'],
          componentRestrictions: { country: ['us', 'ca'] },
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place.address_components) {
            const parsed = parseAddressComponents(place.address_components);
            onPlaceSelect(parsed);
          }
        });

        autocompleteRef.current = autocomplete;
        liveAutocompletes.add(autocomplete);
      } else if (waited < MAX_WAIT_MS) {
        waited += POLL_MS;
        setTimeout(checkGoogle, POLL_MS);
      }
    };

    checkGoogle();
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (autocompleteRef.current) {
        liveAutocompletes.delete(autocompleteRef.current);
        detachAutocomplete(autocompleteRef.current);
      }
    };
  }, []);

  return { initAutocomplete };
}
